import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import { KibuCarePlan } from '@/services/api'
import { KibuCarePlanDisplay } from './KibuCareSelector'
import KibuCareUpgrade from './KibuCareUpgrade'
import { KibuCareOption } from '@/services/orderApi'

interface KibuCareLearnMoreModalProps {
  isOpen: boolean
  onClose: () => void
  // Plan picker — omit all three to hide the plan selection section
  planOptions?: KibuCarePlanDisplay[]
  plans?: KibuCarePlan[]
  initialSelectedPlanId?: string | null
  onAddPlan?: (planId: string) => void
  onNoPlan?: () => void
  onNavigateAway?: () => void
  // Upgrade flow — when provided, shows KibuCareUpgrade instead of plan picker
  orderNumber?: string
  token?: string | null
  onChooseUpgradePlan?: (plan: KibuCareOption) => void
}

const KibuCareLearnMoreModal: React.FC<KibuCareLearnMoreModalProps> = ({
  isOpen,
  onClose,
  planOptions,
  plans,
  initialSelectedPlanId,
  onAddPlan,
  onNoPlan,
  onNavigateAway,
  orderNumber,
  token,
  onChooseUpgradePlan,
}) => {
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(initialSelectedPlanId || null)
  const hasUpgradeFlow = !!(orderNumber && token)
  const hasPlanPicker = !hasUpgradeFlow && !!(planOptions?.length && plans?.length && onAddPlan)

  useEffect(() => {
    if (!isOpen) return
    setSelectedPlanId(initialSelectedPlanId || planOptions?.[0]?.planId || null)
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => {
      document.body.style.overflow = originalOverflow
      document.removeEventListener('keydown', onEsc)
    }
  }, [isOpen])

  if (!isOpen) return null

  const getPlanImage = (planId: string): { src: string | null; alt: string } => {
    const plan = plans?.find((p) => p._id === planId)
    if (!plan) return { src: null, alt: 'Kibu Care' }
    const sorted = (plan.images || []).slice().sort((a, b) => {
      const aO = typeof a === 'object' && a ? (a.order || 0) : 0
      const bO = typeof b === 'object' && b ? (b.order || 0) : 0
      return aO - bO
    })
    const first = sorted[0]
    if (typeof first === 'string') return { src: first, alt: plan.optionTitle || plan.productName || 'Kibu Care' }
    if (first && typeof first === 'object') return { src: first.thumbnailUrl || first.url || null, alt: first.alt || plan.optionTitle || plan.productName || 'Kibu Care' }
    return { src: typeof plan.heroImage === 'string' ? plan.heroImage : null, alt: plan.optionTitle || plan.productName || 'Kibu Care' }
  }

  const activeOption = planOptions?.find((o) => o.planId === selectedPlanId) || planOptions?.[0] || null

  const handleAdd = () => {
    if (selectedPlanId && onAddPlan) onAddPlan(selectedPlanId)
    onClose()
  }

  const handleNoPlan = () => {
    onNoPlan?.()
    onClose()
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Kibu Care details"
        className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-3xl bg-[#f5f5f7] p-5 md:p-8 shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-4 top-4 rounded-full bg-black/10 p-2 text-gray-700 transition-colors hover:bg-black/20"
          aria-label="Close Kibu Care details"
          onClick={onClose}
        >
          <X className="size-5" />
        </button>

        <div className="mx-auto max-w-3xl">
          <div className="mb-6 mt-4 text-center">
            <h2 className="text-3xl font-semibold text-gray-900 md:text-5xl mx-12">Kibu Care for your order</h2>
            <p className="mt-3 text-base text-gray-600 mx-12 md:mx-0">
              Extra peace of mind for everyday family use.
            </p>
            {activeOption && (
              <p className="mt-2 md:mt-0 text-xl font-medium text-gray-800">{activeOption.totalLabel}</p>
            )}
            {activeOption && (
              <p className="text-sm text-gray-500">{activeOption.perUnitLabel}</p>
            )}
          </div>

          <div className="rounded-2xl border border-black/10 bg-white p-5 md:p-6">
            <h3 className="text-xl font-semibold text-gray-900">Why add Kibu Care</h3>
            <ul className="mt-4 list-disc space-y-2 pl-6 text-gray-700">
              <li>Accidental damage support for real-life knocks, drops and spills.</li>
              <li>Functional issues are covered, helping avoid unexpected replacement cost.</li>
              <li>Free replacement parts are included under Kibu Care (shipping fees may apply).</li>
              <li>If a fix is not possible, we will arrange a repair or replacement path with you.</li>
              <li>Repair-first approach keeps headphones in use for longer.</li>
              <li>Fast help from the Kibu team when you need to make a claim.</li>
            </ul>
            <h3 className="mt-6 text-xl font-semibold text-gray-900">Simple claim process</h3>
            <p className="mt-3 text-gray-700">
              Share your order number plus a quick photo or video, and we will guide you through repair or replacement options.
            </p>
            <p className="mt-4 text-sm text-gray-600">
              Terms still apply, including shipping fees and fair-use claim limits. Kibu Care is in addition to your consumer rights.
            </p>
            <Link
              to="/legal/kibu-care-terms/"
              className="mt-4 inline-flex items-center text-sm font-semibold text-gray underline-offset-2 hover:opacity-50 underline hover:underline"
              onClick={() => {
                onClose()
                onNavigateAway?.()
              }}
            >
              Read full Kibu Care terms
            </Link>
          </div>

          {hasUpgradeFlow && (
            <KibuCareUpgrade
              onChoosePlan={onChooseUpgradePlan}
              orderNumber={orderNumber!}
              token={token!}
              variant="modal"
              className="mt-6"
            />
          )}

          {hasPlanPicker && (
            <div className="mt-6 rounded-2xl border border-black/10 bg-white p-5 md:p-6">
              <h3 className="text-xl font-semibold text-gray-900">Choose your plan</h3>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {planOptions!.map((option) => {
                  const isSelected = selectedPlanId === option.planId
                  const planImage = getPlanImage(option.planId)
                  return (
                    <button
                      key={option.planId}
                      type="button"
                      className={`rounded-xl border p-3 text-left transition-colors ${isSelected ? 'border-kibuBlue bg-kibuBlue/10' : 'border-black/15 bg-white hover:border-black/35'}`}
                      onClick={() => setSelectedPlanId(option.planId)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative size-16 shrink-0 overflow-hidden rounded-md bg-slate-100">
                          {planImage.src ? (
                            <img src={planImage.src} alt={planImage.alt} className="h-full w-full object-cover" />
                          ) : (
                            <div className="h-full w-full bg-gradient-to-br from-slate-100 to-slate-200" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-base font-semibold text-gray-900">{option.title}</div>
                          <div className="text-sm text-gray-500">{option.perUnitLabel}</div>
                          <div className="mt-1 text-xl font-semibold text-gray-900">{option.totalLabel}</div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>

              <div className="mt-5 grid gap-2 md:grid-cols-2">
                <button
                  type="button"
                  className={`w-full rounded-xl px-4 py-3 font-semibold text-white transition-colors ${selectedPlanId ? 'bg-kibuBlue hover:bg-kibuBlue/90' : 'cursor-not-allowed bg-gray-400'}`}
                  onClick={handleAdd}
                  disabled={!selectedPlanId}
                >
                  Add Kibu Care to order
                </button>
                <button
                  type="button"
                  className="w-full rounded-xl border border-black/15 bg-white px-4 py-3 font-semibold text-gray-800 transition-colors hover:bg-gray-50"
                  onClick={handleNoPlan}
                >
                  No Kibu Care
                </button>
              </div>
            </div>
          )}

          {!hasUpgradeFlow && !hasPlanPicker && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                className="rounded-xl bg-kibuBlue px-8 py-3 font-semibold text-white transition-colors hover:bg-kibuBlue/90"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default KibuCareLearnMoreModal
